import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AllDeviceInfo, DeviceType, Language, LoginPayload } from '@interfaces';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { RouterFacade } from '@state/router';

import { defaultLanguages } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { DeviceInfoService } from '@core/services/device-info.service';
import { CommonsFacade } from '@state/commons';
import { UserAuthFacade } from '@state/user-auth';
import { BehaviorSubject, combineLatest, of, ReplaySubject } from 'rxjs';
import { delay, delayWhen, distinctUntilChanged, map, startWith, take, takeUntil } from 'rxjs/operators';

interface LoginDeviceView {
  focusMainTitle?(): void;
  focusCredentialInput(field: 'email' | 'password'): void;
  focusMfaInput(index: number): void;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', './login-desktop-shell.component.scss', './login-mobile-shell.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly fb = inject(FormBuilder);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly snackService = inject(StackedMessagesService);
  private readonly routerFacade = inject(RouterFacade);
  private readonly ls = inject(LocalStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly deviceInfoService = inject(DeviceInfoService);

  private readonly mfaCodeLength = 6;
  private resendCooldownInterval: ReturnType<typeof setInterval> | null = null;
  private loginCredentialsCache: LoginPayload | null = null;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly apiAvailable$ = new BehaviorSubject<boolean>(true);
  private readonly languageReady$ = new BehaviorSubject<boolean>(false);
  private _deviceView?: LoginDeviceView;

  @ViewChild('safeLogoutInfoRef') safeLogoutInfoRef!: ElementRef;
  @ViewChild('mainTitleText') private mainTitleText?: ElementRef<HTMLElement>;
  @ViewChild('deviceView')
  private set deviceView(value: LoginDeviceView | undefined) {
    this._deviceView = value;

    if (!value || !this.isDesktopLayout) {
      return;
    }

    setTimeout(() => this.focusMainTitle());
  }

  public loginForm = this.fb.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    password: ['', Validators.compose([Validators.required, Validators.minLength(5)])],
  });

  public readonly mfaDigits = this.fb.array(Array.from({ length: this.mfaCodeLength }, () => this.fb.control('')));
  public readonly mfaCodeControls = this.mfaDigits.controls as FormControl[];
  public mfaForm = this.fb.group({
    digits: this.mfaDigits,
  });

  public errorMessage$ = this.userAuthFacade.errorMessage$;
  public authActionLoading$ = this.userAuthFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(200)))),
    startWith(false)
  );
  public commonsLoading$ = this.commonsFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(500)))),
    startWith(true)
  );
  // public authActionLoading$ = of(true);
  // public commonsLoading$ = of(true);

  public currentUserLang: Language;
  public safeLogoutInfo = false;
  public loginStep: 'CREDENTIALS' | 'MFA' = 'MFA';
  public pendingLoginId: string | null = null;
  public maskedPhoneNumber: string | null = null;
  public mfaErrorMessage: string | null = null;
  public mfaRequestMessage: string | null = null;
  public isMfaCodeComplete = false;
  public resendCooldownSeconds = 0;

  public isPasswordVisible = false;

  public APIAvailable = true;
  public isDesktopLayout = false;
  public viewLoading = true;
  public authActionLoading = false;

  private redirect: string | null = null;
  private viewReadyTimer: ReturnType<typeof setTimeout> | null = null;

  public deviceInfo: AllDeviceInfo;

  public ngOnInit(): void {
    this.setDeviceInfo(this.deviceInfoService.getInfo());

    combineLatest([this.apiAvailable$, this.languageReady$, this.commonsLoading$.pipe(startWith(true))])
      .pipe(
        map(([APIAvailable, languageReady, commonsLoading]) => APIAvailable && (!languageReady || commonsLoading)),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(loading => this.setViewLoading(loading));

    this.commonsFacade.lastAPIPingSuccessfull$.pipe(takeUntil(this.destroy$)).subscribe(APIPingSuccess => {
      if (!APIPingSuccess) {
        // if API connection failed - setup langs with default Languages hardcoded
        this.setupUserLang([...defaultLanguages]);
        this.APIAvailable = false;
        this.apiAvailable$.next(false);
        return;
      }

      this.APIAvailable = true;
      this.apiAvailable$.next(true);
    });

    this.commonsFacade
      .getLanguages$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(availableLanguages => {
        // setup user lang with languages fetched from API
        this.setupUserLang([...availableLanguages]);
        this.APIAvailable = true;
        this.apiAvailable$.next(true);
      });

    const { email, message, redirect, safeLogout } = this.activatedRoute.snapshot.queryParams;
    if (email) {
      this.loginForm.patchValue({ email });
    }

    if (message) {
      this.snackService.showInfo({ message: message });
    }

    if (redirect) {
      this.redirect = redirect;
    }

    if (safeLogout) {
      this.safeLogoutInfo = true;
      this.safeLogoutInfoRef?.nativeElement.focus();
    }

    this.userAuthFacade.loginStep$.pipe(takeUntil(this.destroy$)).subscribe(loginStep => {
      const shouldFocusMfa = this.loginStep !== 'MFA' && loginStep === 'MFA';
      this.loginStep = loginStep;

      if (loginStep !== 'MFA') {
        this.resetMfaForm();
      }

      if (shouldFocusMfa) {
        this.startResendCooldown(60);
        setTimeout(() => this.focusMfaInput(0));
      }
    });

    this.userAuthFacade.pendingLoginId$.pipe(takeUntil(this.destroy$)).subscribe(pendingLoginId => {
      this.pendingLoginId = pendingLoginId;
    });

    this.userAuthFacade.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.authActionLoading = loading;
    });

    this.userAuthFacade.maskedPhoneNumber$.pipe(takeUntil(this.destroy$)).subscribe(maskedPhoneNumber => {
      this.maskedPhoneNumber = maskedPhoneNumber;
    });

    this.userAuthFacade.mfaErrorMessage$.pipe(takeUntil(this.destroy$)).subscribe(mfaErrorMessage => {
      this.mfaErrorMessage = mfaErrorMessage;
    });

    this.userAuthFacade.resendLoginMfaCodeSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.mfaRequestMessage = 'LOGIN_PAGE.MFA_RESEND_SUCCESS';
      this.startResendCooldown(60);
    });

    this.userAuthFacade.loginSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(({ redirect }) => {
      if (redirect) {
        this.routerFacade.changeRoute({ linkParams: ['/init-user'], extras: { queryParams: { redirect } } });
      } else {
        this.routerFacade.changeRoute({ linkParams: ['/init-user'] });
      }
    });
  }

  public closeSafeLogoutInfo(): void {
    this.routerFacade.changeRoute({ linkParams: ['/login'], extras: { replaceUrl: true } });
    this.safeLogoutInfo = false;
  }

  public ngAfterViewInit() {
    this.focusMainTitle();
  }

  public ngOnDestroy(): void {
    this.clearViewReadyTimer();
    this.clearResendCooldown();
    this.loginCredentialsCache = null;

    if (this.loginStep === 'MFA' || this.pendingLoginId) {
      this.userAuthFacade.resetLoginFlow();
    }

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public userLangChanged($event: Language): void {
    this.currentUserLang = $event;
    this.languageReady$.next(!!$event?.key);
  }

  public submitLoginForm(): boolean {
    if (!this.loginForm.valid || this.authActionLoading || !this.currentUserLang?.id) {
      return false;
    }

    this.dismissMobileKeyboard();
    this.loginForm.markAsPristine();
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;
    const redirect = this.redirect;
    this.loginCredentialsCache = {
      email,
      password,
      userLangId: this.currentUserLang.id,
    };
    this.mfaRequestMessage = null;
    this.snackService.closeAll();
    this.userAuthFacade.login(email, password, this.currentUserLang.id, redirect);
    return false;
  }

  public submitMfaForm(): boolean {
    if (!this.isMfaCodeComplete || !this.pendingLoginId || this.authActionLoading) {
      return false;
    }

    this.dismissMobileKeyboard();
    this.userAuthFacade.validateLoginMfa(
      {
        pendingLoginId: this.pendingLoginId,
        code: this.mfaCodeControls.map(control => control.value ?? '').join(''),
      },
      this.redirect
    );

    return false;
  }

  public onMfaDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    const digit = raw.slice(-1);

    input.value = digit;
    this.mfaCodeControls[index].setValue(digit, { emitEvent: false });
    this.mfaErrorMessage = null;
    this.updateMfaCodeCompletion();

    if (digit && index < this.mfaCodeLength - 1) {
      this.focusMfaInput(index + 1);
    }
  }

  public onCredentialsEnter(event: Event, field: 'email' | 'password'): void {
    event.preventDefault();

    if (field === 'email') {
      this.focusCredentialInput('password');
      return;
    }

    this.submitLoginForm();
  }

  public onMfaKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      if (this.mfaCodeControls[index].value) {
        this.mfaCodeControls[index].setValue('', { emitEvent: false });
        this.updateMfaCodeCompletion();
        return;
      }

      if (index > 0) {
        this.focusMfaInput(index - 1);
      }
    }

    if (event.key === 'Enter') {
      this.submitMfaForm();
    }
  }

  public onMfaPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, this.mfaCodeLength);

    this.mfaCodeControls.forEach((control, index) => {
      control.setValue(digits[index] ?? '', { emitEvent: false });
    });

    this.mfaErrorMessage = null;
    this.updateMfaCodeCompletion();
    this.focusMfaInput(Math.min(digits.length, this.mfaCodeLength - 1));
  }

  public backToCredentials(): void {
    this.loginForm.patchValue({ password: '' });
    this.loginForm.controls.password.markAsPristine();
    this.loginForm.controls.password.markAsUntouched();
    this.isPasswordVisible = false;
    this.loginCredentialsCache = null;
    this.resetMfaForm();
    this.userAuthFacade.resetLoginFlow();
  }

  public resendMfaCode(): boolean {
    if (!this.loginCredentialsCache || this.resendCooldownSeconds > 0) {
      return false;
    }

    this.mfaErrorMessage = null;
    this.mfaRequestMessage = null;
    this.userAuthFacade.resendLoginMfaCode(
      this.loginCredentialsCache.email,
      this.loginCredentialsCache.password,
      this.loginCredentialsCache.userLangId,
      this.redirect
    );

    return false;
  }

  private setupUserLang(langs: Language[]): void {
    const { langKey } = this.activatedRoute.snapshot.queryParams;

    // lang from query params is most important
    const langInQueryParams = langs.find(({ key }) => key === langKey);

    // if no lang in query params then localStorage
    const langInLS = this.ls.getUserLang();

    // if no lang in Query & no in LS use default
    const defaultLang = langs.find(({ key }) => key === 'en');

    // 1. langInQuery 2. langInLS 3. defaultLang
    const langToUse = langInQueryParams?.key ? langInQueryParams : langInLS?.key ? langInLS : defaultLang;

    this.ls.setUserLang(langToUse);
    this.translateService.use(langToUse.key);
    this.currentUserLang = langToUse;
    this.languageReady$.next(!!langToUse?.key);
  }

  public scrollToInput(event: FocusEvent) {
    if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
      const target = event.target as HTMLElement;
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }

  private updateMfaCodeCompletion(): void {
    this.isMfaCodeComplete = this.mfaCodeControls.every(control => /^\d$/.test(control.value ?? ''));
  }

  private resetMfaForm(): void {
    this.mfaCodeControls.forEach(control => control.setValue('', { emitEvent: false }));
    this.mfaErrorMessage = null;
    this.mfaRequestMessage = null;
    this.isMfaCodeComplete = false;
    this.resendCooldownSeconds = 0;
    this.clearResendCooldown();
  }

  private focusMfaInput(index: number): void {
    this._deviceView?.focusMfaInput(index);
  }

  private focusCredentialInput(field: 'email' | 'password'): void {
    this._deviceView?.focusCredentialInput(field);
  }

  private dismissMobileKeyboard(): void {
    if (this.deviceInfo.deviceTypeDetected === DeviceType.DESKTOP) {
      return;
    }

    const activeElement = this.document.activeElement;

    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }

  private focusMainTitle(): void {
    if (!this.isDesktopLayout) {
      return;
    }

    this.mainTitleText?.nativeElement.focus();
  }

  private setDeviceInfo(info: AllDeviceInfo): void {
    this.deviceInfo = info;
    this.isDesktopLayout = info.deviceTypeDetected === 'DESKTOP';
  }

  private setViewLoading(loading: boolean): void {
    if (loading) {
      this.clearViewReadyTimer();
      this.viewLoading = true;
      return;
    }

    this.scheduleViewReady();
  }

  private scheduleViewReady(): void {
    if (this.viewReadyTimer || !this.viewLoading) {
      return;
    }

    this.viewReadyTimer = setTimeout(() => {
      this.viewLoading = false;
      this.viewReadyTimer = null;
    });
  }

  private clearViewReadyTimer(): void {
    if (!this.viewReadyTimer) {
      return;
    }

    clearTimeout(this.viewReadyTimer);
    this.viewReadyTimer = null;
  }

  private startResendCooldown(seconds: number): void {
    this.clearResendCooldown();
    this.resendCooldownSeconds = seconds;

    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldownSeconds = Math.max(this.resendCooldownSeconds - 1, 0);

      if (this.resendCooldownSeconds === 0) {
        this.clearResendCooldown();
      }
    }, 1000);
  }

  private clearResendCooldown(): void {
    if (!this.resendCooldownInterval) {
      return;
    }

    clearInterval(this.resendCooldownInterval);
    this.resendCooldownInterval = null;
  }
}
